import { memo } from 'react';
import { ChevronRight } from 'lucide-react';
import { Handle, NodeResizer, Position, type NodeProps } from '@xyflow/react';
import {
  kindLabel,
  layerLabel,
  MAP_CONNECT_SOURCE_HANDLE,
  MAP_CONNECT_TARGET_HANDLE,
  type MapNodeKind,
  type MapNodeLayer,
} from '../../lib/systemMap';
import type { MapAutomationStep } from '../../lib/systemMapAutomation';
import { useSystemMapNodeActions } from './SystemMapNodeActionsContext';
import { anchorSideToPosition, handleStyleForAnchor } from './systemMapNodeGeometry';
import type { SystemMapConnectionHandle } from './SystemMapNode';

export type SystemMapAutomationNodeData = {
  label: string;
  slug: string;
  kind: MapNodeKind;
  layer: MapNodeLayer;
  description: string | null;
  alias: string | null;
  layout: 'inline' | 'chain';
  steps: MapAutomationStep[];
  width: number;
  height: number;
  highlighted: boolean;
  dimmed: boolean;
  connectMode: boolean;
  isConnectSource: boolean;
  connectionHandles: SystemMapConnectionHandle[];
};

function SystemMapAutomationNode({ id, data, selected }: NodeProps) {
  const nodeData = data as SystemMapAutomationNodeData;
  const { onResizeEnd } = useSystemMapNodeActions();
  const highlightRing = nodeData.isConnectSource
    ? 'ring-2 ring-sky-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
    : nodeData.highlighted
      ? 'ring-2 ring-yellow-400 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
      : selected
        ? 'ring-2 ring-emerald-500 ring-offset-2 ring-offset-white dark:ring-offset-gray-900'
        : '';

  return (
    <>
      <NodeResizer
        isVisible={selected && !nodeData.connectMode}
        minWidth={nodeData.layout === 'chain' ? 240 : 280}
        minHeight={nodeData.layout === 'chain' ? 120 : 180}
        maxWidth={nodeData.layout === 'chain' ? 360 : 960}
        maxHeight={nodeData.layout === 'chain' ? 180 : 420}
        lineClassName="!border-emerald-500"
        handleClassName="!w-2 !h-2 !bg-emerald-500 !border-white"
        onResizeEnd={(_event, params) => {
          onResizeEnd(id, params.width, params.height);
        }}
      />
      <div
        className={`w-full h-full box-border rounded-lg border-2 border-indigo-400 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 shadow-sm flex flex-col overflow-hidden transition-opacity ${highlightRing} ${nodeData.dimmed ? 'opacity-35' : 'opacity-100'}`}
      >
        {nodeData.connectionHandles.map((handle) => (
          <Handle
            key={handle.id}
            id={handle.id}
            type={handle.type}
            position={anchorSideToPosition(handle.side)}
            style={handleStyleForAnchor(handle.side, handle.offset)}
            className={`!w-2.5 !h-2.5 !border-2 !border-white ${
              handle.active ? '!bg-sky-500 !scale-125' : '!bg-gray-400'
            }`}
          />
        ))}

        {nodeData.connectMode ? (
          <>
            <Handle
              id={MAP_CONNECT_SOURCE_HANDLE}
              type="source"
              position={Position.Right}
              style={{ top: '50%', transform: 'translate(50%, -50%)' }}
              className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white !opacity-100"
            />
            <Handle
              id={MAP_CONNECT_TARGET_HANDLE}
              type="target"
              position={Position.Left}
              style={{ top: '50%', transform: 'translate(-50%, -50%)' }}
              className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white !opacity-100"
            />
          </>
        ) : null}

        <div className="px-3 py-2 border-b border-indigo-200/80 dark:border-indigo-800/80 shrink-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-white leading-tight truncate">
                {nodeData.label}
              </div>
              {nodeData.alias ? (
                <div className="text-[10px] font-mono text-indigo-700 dark:text-indigo-300 mt-0.5 truncate">
                  {nodeData.alias}
                </div>
              ) : null}
            </div>
            <div className="shrink-0 flex flex-wrap gap-1 justify-end">
              <span className="text-[10px] uppercase tracking-wide font-medium text-gray-600 dark:text-gray-300">
                {kindLabel(nodeData.kind)}
              </span>
              <span className="text-[10px] text-gray-400">·</span>
              <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {layerLabel(nodeData.layer)}
              </span>
            </div>
          </div>
          {nodeData.description ? (
            <p className="mt-1 text-[11px] leading-snug text-gray-600 dark:text-gray-300 line-clamp-2">
              {nodeData.description}
            </p>
          ) : null}
        </div>

        {nodeData.layout === 'chain' ? (
          <div className="flex-1 min-h-0 px-3 py-2 flex items-center">
            <p className="text-[11px] text-indigo-700 dark:text-indigo-300">
              Step previews chain to the right — follow connectors labeled with day offset.
            </p>
          </div>
        ) : null}

        {nodeData.layout !== 'chain' ? (
        <div className="flex-1 min-h-0 px-2 py-2 overflow-x-auto">
          {nodeData.steps.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
              Link an automation and sync steps in the detail panel.
            </p>
          ) : (
            <div className="flex items-stretch gap-1 min-w-max h-full">
              {nodeData.steps.map((step, index) => (
                <div key={step.stepId} className="flex items-stretch gap-1">
                  <div className="w-[124px] flex flex-col rounded-md border border-indigo-200 dark:border-indigo-800 bg-white/80 dark:bg-gray-900/60 overflow-hidden">
                    <div className="px-2 py-1 border-b border-indigo-100 dark:border-indigo-900/80 shrink-0">
                      <div className="text-[10px] font-semibold text-indigo-800 dark:text-indigo-200 leading-tight">
                        {step.scheduleLabel}
                      </div>
                      {step.name ? (
                        <div className="text-[10px] text-gray-600 dark:text-gray-400 leading-tight line-clamp-2 mt-0.5">
                          {step.name}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex-1 min-h-[72px] bg-gray-100 dark:bg-gray-800">
                      {step.screenshotUrl ? (
                        <img
                          src={step.screenshotUrl}
                          alt=""
                          className="w-full h-full object-cover object-top"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400 px-2 text-center">
                          No screenshot
                        </div>
                      )}
                    </div>
                  </div>
                  {index < nodeData.steps.length - 1 ? (
                    <div className="flex items-center text-indigo-400 dark:text-indigo-500">
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
        ) : null}
      </div>
    </>
  );
}

export default memo(SystemMapAutomationNode);
